import { Validator } from 'fluentvalidation-ts';
import { assertNotNull } from '../../../../helpers/nullHelpers';
import { fieldRequiredValidationMessage } from '../../../../validators/sharedValidators';
import {
  beAnInteger,
  beLessThanOrEqualTo,
  bePositiveNumber,
  haveDecimalPlaces,
} from '../../../../validators/stringValidators';
import {
  HeatPumpUnitFormModel,
  MicrogenerationCertificationSchemeLocationFormModel,
} from '../GetDataForQuoteOrDesignForm';
import { GroundCollectorTypeCode, HeatPumpTypeCode, SwimmingPoolTypeCode } from '../quoteOrDesignFormData';

export type HeatPumpFormModel = {
  isExpanded: boolean;
  heatPumpTypeCode: HeatPumpTypeCode;
  groundCollectorTypeCode: GroundCollectorTypeCode;
  groundCollectorLengthInMetres: string;
  closestMicrogenerationCertificationSchemeLocation: MicrogenerationCertificationSchemeLocationFormModel | null;
  isCoastalLocation: boolean;
  shouldAddUserHeatEmitter: boolean;
  hasPrivateWaterSupply: boolean;
  areaInMetresSquared: string;
  heatLossRateInWattsPerSquareMetre: string;
  buildingHeatLoadInKilowatts: string;
  swimmingPoolTypeCode: SwimmingPoolTypeCode;
  swimmingPoolEnergyConsumptionInKilowattHours: string;
  swimmingPoolPowerInKilowatts: string;
  heatPumpUnitParts: Array<HeatPumpUnitFormModel | null>;
  percentageCoverage: number | null;
  dockingCode: string | null;
  requiredHeatPumpUnitCount: number | null;
  supportOptionPartId: number | null;
};

const getInitialHeatPumpFormModel = (): HeatPumpFormModel => ({
  isExpanded: true,
  heatPumpTypeCode: 'AirSource',
  groundCollectorTypeCode: 'TwentyFiveMillimetres',
  groundCollectorLengthInMetres: '',
  closestMicrogenerationCertificationSchemeLocation: null,
  isCoastalLocation: false,
  shouldAddUserHeatEmitter: false,
  hasPrivateWaterSupply: false,
  areaInMetresSquared: '',
  heatLossRateInWattsPerSquareMetre: '',
  buildingHeatLoadInKilowatts: '',
  swimmingPoolTypeCode: 'None',
  swimmingPoolEnergyConsumptionInKilowattHours: '',
  swimmingPoolPowerInKilowatts: '',
  heatPumpUnitParts: [null],
  percentageCoverage: null,
  dockingCode: null,
  requiredHeatPumpUnitCount: null,
  supportOptionPartId: null,
});

export const getInitialHeatPumpFormModelForQuote = (): HeatPumpFormModel => getInitialHeatPumpFormModel();

export const getInitialHeatPumpFormModelForDesign = (calculatedValues: {
  areaInMetresSquared: number | null;
  heatLossRateInWattsPerSquareMetre: number | null;
}): HeatPumpFormModel => ({
  ...getInitialHeatPumpFormModel(),
  areaInMetresSquared: calculatedValues.areaInMetresSquared?.toString() ?? '',
  heatLossRateInWattsPerSquareMetre: calculatedValues.heatLossRateInWattsPerSquareMetre?.toString() ?? '',
});

const requiredHeatPumpUnitCountValidationMessage = (requiredHeatPumpUnitCount: number) =>
  `This docking code requires ${requiredHeatPumpUnitCount} heat pump ${
    requiredHeatPumpUnitCount === 1 ? 'unit' : 'units'
  }`;

export const padHeatPumpUnitPartsToRequiredCount = (
  heatPumpUnitParts: Array<HeatPumpUnitFormModel | null>,
  requiredHeatPumpUnitCount: number | null,
): Array<HeatPumpUnitFormModel | null> =>
  requiredHeatPumpUnitCount == null || heatPumpUnitParts.length >= requiredHeatPumpUnitCount
    ? heatPumpUnitParts
    : [...heatPumpUnitParts, ...new Array(requiredHeatPumpUnitCount - heatPumpUnitParts.length).fill(null)];

export const canAddHeatPumpUnit = (
  heatPumpUnitParts: Array<HeatPumpUnitFormModel | null>,
  requiredHeatPumpUnitCount: number | null,
) => requiredHeatPumpUnitCount == null || heatPumpUnitParts.length < requiredHeatPumpUnitCount;

const selectedHeatPumpUnitCount = (heatPumpUnitParts: Array<HeatPumpUnitFormModel | null>) =>
  heatPumpUnitParts.filter(heatPumpUnit => heatPumpUnit != null).length;

const haveTheNumberOfHeatPumpUnitsRequiredByTheDockingCode = {
  predicate: (_: string | null, heatPump: HeatPumpFormModel) =>
    heatPump.requiredHeatPumpUnitCount == null ||
    selectedHeatPumpUnitCount(heatPump.heatPumpUnitParts) === heatPump.requiredHeatPumpUnitCount,
  message: (_: string | null, heatPump: HeatPumpFormModel) =>
    requiredHeatPumpUnitCountValidationMessage(assertNotNull(heatPump.requiredHeatPumpUnitCount)),
};

export class HeatPumpFormModelValidator extends Validator<HeatPumpFormModel> {
  constructor(isDraft: boolean) {
    super();

    this.ruleFor('groundCollectorLengthInMetres')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .must(bePositiveNumber)
      .when(heatPump => !isDraft && heatPump.heatPumpTypeCode === 'GroundSourceWithGroundLoops');

    this.ruleFor('groundCollectorLengthInMetres')
      .must(beAnInteger)
      .must(beLessThanOrEqualTo(3600))
      .unless(() => isDraft);

    this.ruleFor('closestMicrogenerationCertificationSchemeLocation')
      .notNull()
      .withMessage(fieldRequiredValidationMessage)
      .unless(() => isDraft);

    this.ruleFor('areaInMetresSquared')
      .must(haveDecimalPlaces(1))
      .must(beLessThanOrEqualTo(1000000));

    this.ruleFor('areaInMetresSquared')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .must(bePositiveNumber)
      .unless(() => isDraft);

    this.ruleFor('heatLossRateInWattsPerSquareMetre')
      .must(haveDecimalPlaces(1))
      .must(beLessThanOrEqualTo(1000000));

    this.ruleFor('heatLossRateInWattsPerSquareMetre')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .must(bePositiveNumber)
      .unless(() => isDraft);

    this.ruleFor('buildingHeatLoadInKilowatts')
      .must(haveDecimalPlaces(1))
      .must(beLessThanOrEqualTo(1000000));

    this.ruleFor('buildingHeatLoadInKilowatts')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .must(bePositiveNumber)
      .unless(() => isDraft);

    this.ruleFor('swimmingPoolEnergyConsumptionInKilowattHours')
      .must(haveDecimalPlaces(1))
      .must(beLessThanOrEqualTo(1000000));

    this.ruleFor('swimmingPoolEnergyConsumptionInKilowattHours')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .must(bePositiveNumber)
      .unless(
        heatPump =>
          isDraft ||
          heatPump.swimmingPoolTypeCode === 'None' ||
          heatPump.swimmingPoolEnergyConsumptionInKilowattHours === '',
      );

    this.ruleFor('swimmingPoolPowerInKilowatts')
      .must(haveDecimalPlaces(1))
      .must(beLessThanOrEqualTo(1000000));

    this.ruleFor('swimmingPoolPowerInKilowatts')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .must(bePositiveNumber)
      .unless(
        heatPump => isDraft || heatPump.swimmingPoolTypeCode === 'None' || heatPump.swimmingPoolPowerInKilowatts === '',
      );

    this.ruleForEach('heatPumpUnitParts')
      .notNull()
      .withMessage(fieldRequiredValidationMessage)
      .unless(() => isDraft);

    this.ruleForEach('heatPumpUnitParts')
      .must(heatPumpUnit => heatPumpUnit == null || heatPumpUnit.isAirSourceHeatPump)
      .withMessage('Please select an air source heat pump unit')
      .when(heatPump => heatPump.heatPumpTypeCode === 'AirSource');

    this.ruleForEach('heatPumpUnitParts')
      .must(heatPumpUnit => heatPumpUnit == null || !heatPumpUnit.isAirSourceHeatPump)
      .withMessage('Please select a ground source heat pump unit')
      .when(heatPump => heatPump.heatPumpTypeCode !== 'AirSource');

    this.ruleForEach('heatPumpUnitParts')
      .must(heatPumpUnit => heatPumpUnit == null || heatPumpUnit.suitableForCoastalLocation)
      .withMessage('Not suitable for a coastal location')
      .when(heatPump => heatPump.heatPumpTypeCode === 'AirSource' && heatPump.isCoastalLocation);

    this.ruleFor('dockingCode')
      .notNull()
      .withMessage(fieldRequiredValidationMessage)
      .unless(() => isDraft);

    this.ruleFor('dockingCode')
      .must(haveTheNumberOfHeatPumpUnitsRequiredByTheDockingCode)
      .unless(() => isDraft);

    this.ruleFor('supportOptionPartId')
      .notNull()
      .withMessage(fieldRequiredValidationMessage);
  }
}
